import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Pause,
  RotateCcw,
  Star,
  Trophy,
  Clock,
  Target,
  DollarSign,
  TrendingUp,
  Coins,
} from "lucide-react";
import { getCurrentUser } from "../utils/auth";
import Layout from "../components/Layout";

interface GameCard {
  id: number;
  content: string;
  isFlipped: boolean;
  isMatched: boolean;
}

interface InvestmentOption {
  id: string;
  name: string;
  risk: "low" | "medium" | "high";
  return: number;
  description: string;
}

const GamePage: React.FC = () => {
  const { gameType } = useParams();
  const [user] = useState(getCurrentUser());
  const navigate = useNavigate();

  // Estados do jogo da memória
  const [memoryCards, setMemoryCards] = useState<GameCard[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState(0);
  const [moves, setMoves] = useState(0);
  const [gameTime, setGameTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);

  // Estados do jogo de investimentos
  const [investmentBudget] = useState(10000);
  const [selectedInvestments, setSelectedInvestments] = useState<{
    [key: string]: number;
  }>({});
  const [simulationResults, setSimulationResults] = useState<any>(null);
  const [simulationCompleted, setSimulationCompleted] = useState(false);

  // Dados dos jogos
  const memoryTerms = [
    { term: "Budget", definition: "Financial plan" },
    { term: "Savings", definition: "Money reserve" },
    { term: "Interest", definition: "Cost of money" },
    { term: "Inflation", definition: "Price increase" },
    { term: "Investment", definition: "Capital application" },
    { term: "Debt", definition: "Financial obligation" },
  ];

  const investmentOptions: InvestmentOption[] = [
    {
      id: "poupanca",
      name: "Savings",
      risk: "low",
      return: 0.5,
      description: "Low risk, low return",
    },
    {
      id: "cdb",
      name: "CDB",
      risk: "low",
      return: 1.2,
      description: "Safe fixed income",
    },
    {
      id: "tesouro",
      name: "Selic Treasury",
      risk: "low",
      return: 1.0,
      description: "Public bond",
    },
    {
      id: "fundo",
      name: "Multi-market Fund",
      risk: "medium",
      return: 2.5,
      description: "Moderate risk",
    },
    {
      id: "acoes",
      name: "Stocks",
      risk: "high",
      return: 4.0,
      description: "High risk, high return",
    },
    {
      id: "cripto",
      name: "Cryptocurrencies",
      risk: "high",
      return: 6.0,
      description: "Very volatile",
    },
  ];

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (gameType === "memory") {
      initializeMemoryGame();
    }
  }, [user, navigate, gameType]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && gameType === "memory") {
      interval = setInterval(() => {
        setGameTime((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameType]);

  const initializeMemoryGame = () => {
    const cards: GameCard[] = [];

    // Criar pares de cartas
    memoryTerms.forEach((term, index) => {
      cards.push(
        {
          id: index * 2,
          content: term.term,
          isFlipped: false,
          isMatched: false,
        },
        {
          id: index * 2 + 1,
          content: term.definition,
          isFlipped: false,
          isMatched: false,
        }
      );
    });

    // Embaralhar cartas
    const shuffledCards = cards.sort(() => Math.random() - 0.5);
    setMemoryCards(shuffledCards);
    setFlippedCards([]);
    setMatchedPairs(0);
    setMoves(0);
    setGameTime(0);
    setGameCompleted(false);
  };

  const handleCardClick = (cardId: number) => {
    if (
      flippedCards.length >= 2 ||
      memoryCards[cardId].isFlipped ||
      memoryCards[cardId].isMatched
    ) {
      return;
    }

    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    const updatedCards = memoryCards.map((card) =>
      card.id === cardId ? { ...card, isFlipped: true } : card
    );
    setMemoryCards(updatedCards);

    if (newFlippedCards.length === 2) {
      setMoves((prev) => prev + 1);

      setTimeout(() => {
        const [firstId, secondId] = newFlippedCards;
        const firstCard = memoryCards.find((c) => c.id === firstId);
        const secondCard = memoryCards.find((c) => c.id === secondId);

        if (firstCard && secondCard) {
          const termIndex = memoryTerms.findIndex(
            (t) =>
              t.term === firstCard.content || t.definition === firstCard.content
          );
          const isMatch =
            (firstCard.content === memoryTerms[termIndex].term &&
              secondCard.content === memoryTerms[termIndex].definition) ||
            (firstCard.content === memoryTerms[termIndex].definition &&
              secondCard.content === memoryTerms[termIndex].term);

          if (isMatch) {
            const matchedCards = updatedCards.map((card) =>
              card.id === firstId || card.id === secondId
                ? { ...card, isMatched: true, isFlipped: false }
                : card
            );
            setMemoryCards(matchedCards);
            setMatchedPairs((prev) => prev + 1);

            if (matchedPairs + 1 === memoryTerms.length) {
              setGameCompleted(true);
              setIsPlaying(false);
            }
          } else {
            const resetCards = updatedCards.map((card) =>
              card.id === firstId || card.id === secondId
                ? { ...card, isFlipped: false }
                : card
            );
            setMemoryCards(resetCards);
          }
        }

        setFlippedCards([]);
      }, 1000);
    }
  };

  const handleInvestmentChange = (investmentId: string, amount: number) => {
    setSelectedInvestments((prev) => ({
      ...prev,
      [investmentId]: amount,
    }));
  };

  const runInvestmentSimulation = () => {
    const results = investmentOptions.map((option) => {
      const amount = selectedInvestments[option.id] || 0;
      const monthlyReturn = (amount * option.return) / 100;
      const yearlyReturn = monthlyReturn * 12;

      return {
        ...option,
        amount,
        monthlyReturn,
        yearlyReturn,
        totalReturn: amount + yearlyReturn,
      };
    });

    setSimulationResults(results);
    setSimulationCompleted(true);
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "low":
        return "text-green-400";
      case "medium":
        return "text-yellow-400";
      case "high":
        return "text-red-400";
      default:
        return "text-gray-400";
    }
  };

  const getRiskIcon = (risk: string) => {
    switch (risk) {
      case "low":
        return "🟢";
      case "medium":
        return "🟡";
      case "high":
        return "🔴";
      default:
        return "⚪";
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const calculateMemoryScore = () => {
    const timeBonus = Math.max(0, 300 - gameTime); // Bônus por tempo
    const movesBonus = Math.max(0, 20 - moves); // Bônus por movimentos
    return Math.max(0, 100 + timeBonus + movesBonus);
  };

  if (!user) {
    return null;
  }

  if (gameType === "memory") {
    if (gameCompleted) {
      const score = calculateMemoryScore();
      const stars = Math.min(3, Math.floor(score / 100));

      return (
        <Layout
          title="Memory Game Completed!"
          subtitle="Congratulations on completing the game"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto"
          >
            <div className="card text-center mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-6xl mb-4"
              >
                <Trophy className="w-16 h-16 text-yellow-400 mx-auto" />
              </motion.div>

              <h2 className="text-2xl font-bold text-white mb-2">
                Excellent work!
              </h2>

              <p className="text-gray-400 mb-6">
                You completed the financial memory game
              </p>

              {/* Estrelas conquistadas */}
              <div className="flex justify-center space-x-2 mb-6">
                {[1, 2, 3].map((star) => (
                  <motion.div
                    key={star}
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{
                      scale: star <= stars ? 1 : 0.5,
                      rotate: 0,
                    }}
                    transition={{
                      duration: 0.3,
                      delay: 0.5 + star * 0.1,
                    }}
                    className={`text-3xl ${
                      star <= stars ? "text-yellow-400" : "text-gray-600"
                    }`}
                  >
                    <Star className="w-8 h-8 text-yellow-400" />
                  </motion.div>
                ))}
              </div>

              {/* Estatísticas */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-dark-700 rounded-lg p-4">
                  <div className="text-2xl font-bold text-white">
                    {formatTime(gameTime)}
                  </div>
                  <div className="text-sm text-gray-400">Time</div>
                </div>
                <div className="bg-dark-700 rounded-lg p-4">
                  <div className="text-2xl font-bold text-white">{moves}</div>
                  <div className="text-sm text-gray-400">Moves</div>
                </div>
                <div className="bg-dark-700 rounded-lg p-4">
                  <div className="text-2xl font-bold text-white">{score}</div>
                  <div className="text-sm text-gray-400">Score</div>
                </div>
              </div>

              {/* Recompensas */}
              <div className="bg-gradient-to-r from-primary-600/20 to-blue-600/20 border border-primary-500/30 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-white mb-3">
                  Rewards Conquered
                </h3>
                <div className="flex justify-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <span className="text-white font-semibold">
                      +{stars * 15}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Coins className="w-6 h-6 text-green-400" />
                    <span className="text-white font-semibold">
                      +{Math.floor(score / 10)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Botões de ação */}
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate("/finance-school")}
                  className="flex-1 py-3 bg-dark-700 hover:bg-dark-600 text-white font-medium rounded-lg transition-colors"
                >
                  Back to School
                </button>
                <button
                  onClick={initializeMemoryGame}
                  className="flex-1 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                >
                  Play Again
                </button>
              </div>
            </div>
          </motion.div>
        </Layout>
      );
    }

    return (
      <Layout
        title="Financial Memory Game"
        subtitle="Find pairs of financial terms"
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          {/* Header do jogo */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate("/finance-school")}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-primary-400" />
                <span className="text-white font-mono">
                  {formatTime(gameTime)}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4 text-primary-400" />
                <span className="text-white font-semibold">{moves}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Trophy className="w-4 h-4 text-yellow-400" />
                <span className="text-white font-semibold">
                  {matchedPairs}/{memoryTerms.length}
                </span>
              </div>
            </div>
          </div>

          {/* Controles do jogo */}
          <div className="flex justify-center mb-6">
            <div className="flex space-x-3">
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                <span>{isPlaying ? "Pause" : "Start"}</span>
              </button>
              <button
                onClick={initializeMemoryGame}
                className="flex items-center space-x-2 px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white font-medium rounded-lg transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Restart</span>
              </button>
            </div>
          </div>

          {/* Grid do jogo */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {memoryCards.map((card, index) => (
              <motion.div
                key={card.id}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => handleCardClick(index)}
                className={`aspect-square rounded-lg cursor-pointer transition-all duration-300 ${
                  card.isFlipped || card.isMatched
                    ? "bg-primary-600 text-white"
                    : "bg-dark-700 hover:bg-dark-600 text-gray-400"
                } ${card.isMatched ? "ring-2 ring-green-500" : ""}`}
              >
                <div className="w-full h-full flex items-center justify-center p-2">
                  <span className="text-sm font-medium text-center">
                    {card.isFlipped || card.isMatched ? card.content : "?"}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Instruções */}
          <div className="mt-8 text-center">
            <p className="text-gray-400 text-sm">
              Click on the cards to find the pairs of financial terms.
            </p>
          </div>
        </motion.div>
      </Layout>
    );
  }

  if (gameType === "investment-race") {
    return (
      <Layout
        title="Investment Simulator"
        subtitle="Build your portfolio and see the results"
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate("/finance-school")}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>

            <div className="flex items-center space-x-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              <span className="text-white font-semibold">
                R$ {investmentBudget.toLocaleString("pt-BR")}
              </span>
            </div>
          </div>

          {!simulationCompleted ? (
            <>
              {/* Opções de investimento */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {investmentOptions.map((option) => (
                  <div key={option.id} className="card">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-white">
                        {option.name}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">
                          {getRiskIcon(option.risk)}
                        </span>
                        <span
                          className={`text-sm font-medium ${getRiskColor(
                            option.risk
                          )}`}
                        >
                          {option.risk === "low" && "Low Risk"}
                          {option.risk === "medium" && "Medium Risk"}
                          {option.risk === "high" && "High Risk"}
                        </span>
                      </div>
                    </div>

                    <p className="text-gray-400 text-sm mb-3">
                      {option.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-green-400 font-semibold">
                        +{option.return}% a.m.
                      </span>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          max={investmentBudget}
                          value={selectedInvestments[option.id] || 0}
                          onChange={(e) =>
                            handleInvestmentChange(
                              option.id,
                              Number(e.target.value)
                            )
                          }
                          className="w-24 px-2 py-1 bg-dark-700 border border-gray-600 rounded text-white text-sm"
                          placeholder="0"
                        />
                        <span className="text-gray-400 text-sm">R$</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Resumo da carteira */}
              <div className="card mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Portfolio Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">
                      R${" "}
                      {Object.values(selectedInvestments)
                        .reduce((sum, val) => sum + val, 0)
                        .toLocaleString("pt-BR")}
                    </div>
                    <div className="text-sm text-gray-400">Total Invested</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400">
                      R${" "}
                      {(
                        investmentBudget -
                        Object.values(selectedInvestments).reduce(
                          (sum, val) => sum + val,
                          0
                        )
                      ).toLocaleString("pt-BR")}
                    </div>
                    <div className="text-sm text-gray-400">Available</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-400">
                      {
                        Object.keys(selectedInvestments).filter(
                          (key) => selectedInvestments[key] > 0
                        ).length
                      }
                    </div>
                    <div className="text-sm text-gray-400">Investments:</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-400">
                      {Object.values(selectedInvestments).reduce(
                        (sum, val) => sum + val,
                        0
                      ) > 0
                        ? Math.round(
                            (Object.values(selectedInvestments).reduce(
                              (sum, val) => sum + val,
                              0
                            ) /
                              investmentBudget) *
                              100
                          )
                        : 0}
                      %
                    </div>
                    <div className="text-sm text-gray-400">Diversification</div>
                  </div>
                </div>
              </div>

              {/* Botão de simulação */}
              <div className="text-center">
                <button
                  onClick={runInvestmentSimulation}
                  disabled={
                    Object.values(selectedInvestments).reduce(
                      (sum, val) => sum + val,
                      0
                    ) === 0
                  }
                  className="px-8 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center space-x-2 mx-auto"
                >
                  <TrendingUp className="w-5 h-5" />
                  <span>Simulate Investments</span>
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Resultados da simulação */}
              <div className="card mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Simulation Results (12 months)
                </h3>
                <div className="space-y-4">
                  {simulationResults?.map(
                    (result: any) =>
                      result.amount > 0 && (
                        <div
                          key={result.id}
                          className="flex items-center justify-between p-4 bg-dark-700 rounded-lg"
                        >
                          <div>
                            <h4 className="text-white font-medium">
                              {result.name}
                            </h4>
                            <p className="text-gray-400 text-sm">
                              Investido: R${" "}
                              {result.amount.toLocaleString("pt-BR")}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-green-400 font-semibold">
                              R$ {result.totalReturn.toLocaleString("pt-BR")}
                            </div>
                            <div className="text-sm text-gray-400">
                              +R$ {result.yearlyReturn.toLocaleString("pt-BR")}{" "}
                              of profit
                            </div>
                          </div>
                        </div>
                      )
                  )}
                </div>
              </div>

              {/* Resumo final */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="card text-center">
                  <div className="text-2xl font-bold text-white">
                    R${" "}
                    {simulationResults
                      ?.reduce(
                        (sum: number, result: any) => sum + result.amount,
                        0
                      )
                      .toLocaleString("pt-BR")}
                  </div>
                  <div className="text-sm text-gray-400">Total Invested</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold text-green-400">
                    R${" "}
                    {simulationResults
                      ?.reduce(
                        (sum: number, result: any) => sum + result.yearlyReturn,
                        0
                      )
                      .toLocaleString("pt-BR")}
                  </div>
                  <div className="text-sm text-gray-400">Annual Profit</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold text-blue-400">
                    {simulationResults
                      ?.reduce(
                        (sum: number, result: any) => sum + result.totalReturn,
                        0
                      )
                      .toLocaleString("pt-BR")}
                  </div>
                  <div className="text-sm text-gray-400">Final Value</div>
                </div>
              </div>

              {/* Recompensas */}
              <div className="bg-gradient-to-r from-primary-600/20 to-blue-600/20 border border-primary-500/30 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-white mb-3">
                  Rewards Conquered
                </h3>
                <div className="flex justify-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <span className="text-white font-semibold">+25</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Coins className="w-6 h-6 text-green-400" />
                    <span className="text-white font-semibold">+50</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                    <span className="text-white font-semibold">Investor!</span>
                  </div>
                </div>
              </div>

              {/* Botões de ação */}
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate("/finance-school")}
                  className="flex-1 py-3 bg-dark-700 hover:bg-dark-600 text-white font-medium rounded-lg transition-colors"
                >
                  Back to Finance School
                </button>
                <button
                  onClick={() => {
                    setSimulationResults(null);
                    setSimulationCompleted(false);
                    setSelectedInvestments({});
                  }}
                  className="flex-1 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                >
                  New Simulation
                </button>
              </div>
            </>
          )}
        </motion.div>
      </Layout>
    );
  }

  return (
    <Layout title="Jogos Financeiros" subtitle="Escolha um jogo para jogar">
      <div className="max-w-2xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="card cursor-pointer"
            onClick={() => navigate("/game/memory")}
          >
            <div className="text-center">
              <div className="text-4xl mb-4">🧠</div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Memory Game
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Find the pairs of financial terms and test your memory
              </p>
              <div className="flex items-center justify-center space-x-2 text-yellow-400">
                <Star className="w-4 h-4" />
                <span className="text-sm">+15 stars</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="card cursor-pointer"
            onClick={() => navigate("/game/investment-race")}
          >
            <div className="text-center">
              <div className="text-4xl mb-4">📈</div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Investment Simulator
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Build your investment portfolio and see the results
              </p>
              <div className="flex items-center justify-center space-x-2 text-yellow-400">
                <Star className="w-4 h-4" />
                <span className="text-sm">+25 stars</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default GamePage;
